// Implements: F-UIX-018
/**
 * The audit list's pure parts (UIX.7): whose trail the person is offered, the range check, and
 * how an actor and an instant read. Plain data in, plain data out.
 *
 * **Targets** come from the server's permission answer (UIX.5): an organization scope carrying
 * `audit:read_organization` (its org admin, the only reader the server allows). No role name is
 * compared. This decides what the page OFFERS; the server decides every read again and answers a
 * reader it refuses with the `404` of a path that does not exist.
 */

import type { BackofficeScope } from "../../../types/api";
import type { AuditFilters, AuditRowView, AuditTarget } from "../../../types/audit";
import type { MockupCostRowView } from "../../../types/reports";
import { PLATFORM_HOUR_CYCLE, PLATFORM_LOCALE } from "../../../types/locale";
import { permits } from "./sections";

export type { AuditFilters, AuditTarget };

/** The organizations the person may read the trail of, in session order. */
export function auditTargetsOf(scopes: readonly BackofficeScope[]): AuditTarget[] {
  return scopes.flatMap((scope) =>
    scope.kind === "organization" && scope.id !== null && permits(scope, "audit:read_organization")
      ? [{ organizationId: scope.id, name: scope.name, namesMockups: permits(scope, "usage:read_organization") }]
      : [],
  );
}

export const NO_FILTERS: AuditFilters = { from: "", to: "", action: "", teamId: "", mockupId: "" };

/** The longest range the server accepts, both days included. */
const MAX_RANGE_DAYS = 366;
const DAY_MS = 86_400_000;

/** What is wrong with a typed range, checked only when both days are given. */
export function rangeProblem(from: string, to: string): "reversed" | "tooLong" | null {
  if (from === "" || to === "") return null;
  const [start, end] = [Date.parse(`${from}T00:00:00Z`), Date.parse(`${to}T00:00:00Z`)];
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  if (start > end) return "reversed";
  return (end - start) / DAY_MS + 1 > MAX_RANGE_DAYS ? "tooLong" : null;
}

export type ActorLabel =
  | { readonly kind: "name"; readonly name: string }
  | { readonly kind: "deleted" }
  | { readonly kind: "system" }
  | { readonly kind: "visitor" }
  | { readonly kind: "nobody" };

/**
 * Who a row says did it: a person by name, an erased person (whatever the server wrote), the
 * system (a revocation no person made), a visitor of a link (a wrong share password), or nobody.
 */
export function actorOf(row: AuditRowView): ActorLabel {
  if (row.actor_deleted) return { kind: "deleted" };
  if (row.actor !== null) return { kind: "name", name: row.actor };
  if (row.action === "share_link_revoked_by_system") return { kind: "system" };
  if (row.action === "share_password_failed") return { kind: "visitor" };
  return { kind: "nobody" };
}

/**
 * `13 oct. 2026, 21:17 UTC`: the instant in the browser's zone, which is named, in the platform
 * locale on the 24-hour clock whatever the browser's language (`types/locale.ts`, S12b-4).
 */
const INSTANT_TEXT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: PLATFORM_HOUR_CYCLE,
  timeZoneName: "short",
});

export function instantText(raw: string): string {
  const instant = Date.parse(raw);
  return Number.isNaN(instant) ? raw : INSTANT_TEXT.format(new Date(instant));
}

/** A mockup as the audit list names it: its name and whether it is in the trash. */
export interface NamedMockup {
  readonly id: string;
  readonly name: string;
  readonly trashed: boolean;
}

/**
 * The mockups named so far, with the ones these rows name added (7.S13.12). The same map comes back
 * when the rows name nothing new, so a caller can keep it as state and update it only on a change.
 */
export function withSeenMockups(
  seen: ReadonlyMap<string, NamedMockup>,
  rows: readonly AuditRowView[],
): ReadonlyMap<string, NamedMockup> {
  let next: Map<string, NamedMockup> | null = null;
  for (const row of rows) {
    if (row.mockup_id === null || row.mockup_name === null) continue;
    const known = (next ?? seen).get(row.mockup_id);
    if (known?.name === row.mockup_name && known.trashed === row.mockup_trashed) continue;
    next ??= new Map(seen);
    next.set(row.mockup_id, { id: row.mockup_id, name: row.mockup_name, trashed: row.mockup_trashed });
  }
  return next ?? seen;
}

/**
 * Every mockup the list can name and the filter offers: the available ones of the organization's
 * per-mockup report, and those the trail itself named (which win, being read with the trail), in
 * name order of the platform locale.
 */
export function namedMockupsOf(
  reportRows: readonly MockupCostRowView[],
  seen: ReadonlyMap<string, NamedMockup>,
): NamedMockup[] {
  const named = new Map<string, NamedMockup>();
  for (const row of reportRows) {
    if (row.mockup.status === "available") {
      named.set(row.mockup.id, { id: row.mockup.id, name: row.mockup.name, trashed: false });
    }
  }
  for (const mockup of seen.values()) named.set(mockup.id, mockup);
  return [...named.values()].sort(
    (a, b) => a.name.localeCompare(b.name, PLATFORM_LOCALE) || a.id.localeCompare(b.id, PLATFORM_LOCALE),
  );
}
